import { invoke } from '@tauri-apps/api/core';
import { HOST_TO_GUEST, GUEST_TO_HOST, RPC_CALL_TIMEOUT_MS, makeEnvelope } from './protocol';
import { useExtensionStore } from '../store';
import { openDbManagerTab, openDbManagerQueryTab } from '../../store/tabsStore';
import { openDbOverviewTab } from '../../store/dbOverviewTabs';
import { reloadDbManagerTables } from '../../store/dbTableReload';
import { notifyFromExtension } from './extensionNotify';
import { getExtensionStorageValue, setExtensionStorageValue } from '../storageFiles';
import { DB_MANAGER_EXTENSION_ID, invalidateSchemaCache } from '../dbEngines';
import { copyTableData } from '../dbCopyTable';
import { useNotificationStore } from '../../store/notificationStore';
import { getBridge } from '../store';
import { startDbVaultChangeRelay } from './dbVaultChangeRelay';
import { isTauri } from '../../utils/tauri';

import {
  connectDbFromSavedConfig,
  deleteSavedDbConnection,
  readSavedDbConnection,
} from '../capabilities/db';
import {
  openVaultGate,
  vaultErrorToken,
  VAULT_GATE_UNLOCKED_EVENT,
  VAULT_GATE_DISMISSED_EVENT,
  VAULT_LOCKED_UI_EVENT,
  VAULT_UNLOCKED_UI_EVENT,
  useVaultStore,
} from '../../store/vaultStore';
import {
  listDbConnectionEnvelopes,
  descriptorFromVaultItem,
  setDbConnectionPassword,
  saveDbConnectionToVault,
} from '../capabilities/dbVaultConnections';
import { UI_MODAL_CAPABILITY, UI_PASSWORD_FIELD_CAPABILITY } from '../registry';
import { orderDbConnections, onProjectScopeChange, joinActiveProjectIfNew } from '../../scope/dbConnectionsProjectOrder';

// Project scope (card 5.3): a new DB connection joins the active project.
const joinActiveProject = (id, descriptor) => {
  joinActiveProjectIfNew(id, descriptor);
};

/**
 * Connection-storage methods, id-gated to the DB Manager.
 *
 * Deliberately an exact set. `db.` is also the prefix of every engine call
 * (db.postgres.connect, db.mysql.query, …), so matching on the prefix breaks
 * all of them.
 */
const DB_CONNECTION_METHODS = new Set([
  'db.listSavedConnections',
  'db.saveConnection',
  'db.connectSaved',
  'db.unlockVault',
  'db.savePassword',
  'db.deleteConnection',
  'db.getConnectionDetails',
  'db.invalidateSchemaCache',
  'db.copyTableData',
]);

/** True if any field in an `ui.openModal` spec asks for a masked secret. */
export function specRequestsPassword(spec) {
  return (spec?.fields || []).some((field) => field?.type === 'password');
}

/**
 * Host-side counterpart to guestSdk.js, one instance per mounted
 * ExtensionHost iframe. Owns the postMessage listener for that iframe,
 * enforces the permission grant set before dispatching any RPC call, and
 * provides the framework-level `storage.get`/`storage.set` methods (backed
 * by a per-extension storage.json file in the app data dir — see
 * storageFiles.js — with a localStorage fallback outside Tauri).
 *
 * Capability calls (e.g. `db.query`) are looked up in `capabilities`, a map
 * of `{ [capabilityString]: { methods: { [method]: (extensionId, params) => Promise } } }`.
 * A method is only reachable if its owning capability is present in
 * `grantedCapabilities` — never trusted from the guest payload.
 */
/**
 * Push a connections-changed notice to the DB Manager guest when the vault
 * opens — and when it LOCKS.
 *
 * Without the first, the sidebar keeps showing the locked row after a
 * successful unlock; without the second, it shows nothing at all when the
 * vault locks mid-session — the open connection sits there with no indication
 * that everything behind it is now locked, while the FTP sidebar (host-
 * rendered, store-driven) flips immediately. The guest is a keep-alive iframe
 * that asked once, at activation; both directions have to be told.
 *
 * Registered once at module load, not per bridge, because the events are
 * global and a per-bridge listener would fire N times.
 */
if (typeof window !== 'undefined') {
  window.addEventListener(VAULT_GATE_UNLOCKED_EVENT, () => {
    getBridge(DB_MANAGER_EXTENSION_ID)?.postToGuest('__db_connections_changed__', {});
  });
  // The one that actually covers most unlocks. VAULT_GATE_UNLOCKED_EVENT above
  // fires only for the passphrase modal; every silent `vault_unlock` — the
  // vault panel, the FTP sidebar, this bridge's own db.unlockVault — announces
  // itself here instead. Both are kept: the gate event also means "the modal
  // finished" to callers waiting to retry, which is a different question.
  //
  // Duplicate pushes are harmless: the guest re-reads connections, which is
  // idempotent.
  window.addEventListener(VAULT_UNLOCKED_UI_EVENT, () => {
    getBridge(DB_MANAGER_EXTENSION_ID)?.postToGuest('__db_connections_changed__', {});
  });
  window.addEventListener(VAULT_LOCKED_UI_EVENT, () => {
    getBridge(DB_MANAGER_EXTENSION_ID)?.postToGuest('__db_connections_changed__', {});
  });
  // …and when the vault's items change: added/edited/deleted here, or synced in.
  if (isTauri()) startDbVaultChangeRelay();
  // Project scope (card 5.3): the guest re-reads (and re-orders) on a switch.
  onProjectScopeChange(() => getBridge(DB_MANAGER_EXTENSION_ID)?.postToGuest('__db_connections_changed__', {}));
}

// Guest-supplied ids, bounded: strings only, at most 20 of at most 512 chars.
// Expanding is harmless, but nothing from the sandbox is taken unchecked.
export function treeExpandIds(nodeIds) {
  if (!Array.isArray(nodeIds)) return [];
  return nodeIds.filter((id) => typeof id === 'string' && id.length > 0 && id.length <= 512).slice(0, 20);
}

/**
 * Run a table copy for the DB Manager: resolves when writing starts (or
 * rejects with the reason it cannot), then shows progress with Cancel and the
 * outcome as notifications, and re-reads open tabs on the target table.
 */
function startTableCopy(extensionId, params) {
  const { source, target, options } = params || {};
  const notes = useNotificationStore.getState();
  let cancelled = false;
  let noteId = null;
  const label = target?.table || 'the target';
  return new Promise((resolve, reject) => {
    copyTableData({
      call: (command, args) => invoke(command, args),
      extensionId,
      source,
      target,
      options,
      isCancelled: () => cancelled,
      onStart: (info) => {
        noteId = notes.addNotification({
          type: 'info',
          title: 'Copying data',
          message: `Copying into ${label}…`,
          duration: 0,
          action: info.sameConnection ? undefined : { label: 'Cancel', onClick: () => { cancelled = true; } },
        });
        resolve({ started: true });
      },
      onProgress: ({ copied, total }) => {
        if (noteId != null) {
          useNotificationStore.getState().updateNotification(noteId, {
            message: `${copied.toLocaleString()} of ${Number(total || 0).toLocaleString()} rows copied into ${label}…`,
          });
        }
      },
    })
      .then((res) => {
        if (noteId != null) useNotificationStore.getState().removeNotification(noteId);
        const n = Number(res.copied || 0).toLocaleString();
        if (res.cancelled) useNotificationStore.getState().warning(`Stopped after ${n} rows copied into ${label}.`, 'Copy cancelled');
        else useNotificationStore.getState().success(`${n} rows copied into ${label}.`, 'Data copied');
        invalidateSchemaCache(extensionId, String(target.connId));
        reloadDbManagerTables({ extensionId, connId: target.connId, dbName: target.db, tableName: target.table });
      })
      .catch((e) => {
        if (noteId == null) return reject(e); // before any write: the guest shows it
        useNotificationStore.getState().removeNotification(noteId);
        useNotificationStore.getState().error(String(e?.message || e), 'Copy failed');
        return undefined;
      });
  });
}

export function createHostBridge({
  extensionId,
  iframeEl,
  frameToken,
  grantedCapabilities = [],
  capabilities = {},
  onTreePublish,
  onTreeExpand,
  onGuestError,
  onGuestReady,
  onGuestDisposedAck,
}) {
  if (typeof frameToken !== 'string' || frameToken.length === 0) {
    // Same refusal as buildGuestSdkSource: a bridge without the per-mount
    // secret would accept/emit unauthenticated envelopes.
    throw new Error('createHostBridge requires a frameToken');
  }
  let grantedSet = new Set(grantedCapabilities);
  // Calls that resolve only on user interaction (a modal the user may sit on
  // for minutes) must not be subject to RPC_CALL_TIMEOUT_MS — timing one out
  // rejects the guest's promise while the modal is still open, so the user's
  // eventual submit would be silently dropped.
  const interactiveMethods = new Set(['ui.openModal']);
  const pendingTimeouts = new Map();
  const modalResolvers = new Map();
  let modalSeq = 0;
  // The one modal request THIS extension currently has on screen, if any.
  // Used to supersede its own stale modal without ever touching another
  // extension's — see the `ui.openModal` handler.
  let activeModalRequestId = null;

  /**
   * Resolves this extension's own on-screen modal (if any) with `null` and
   * clears the store slot, so a second `ui.openModal` from the same guest
   * can't leave the first promise hanging forever.
   */
  function supersedeOwnModal() {
    if (!activeModalRequestId) return;
    const resolve = modalResolvers.get(activeModalRequestId);
    modalResolvers.delete(activeModalRequestId);
    activeModalRequestId = null;
    useExtensionStore.getState().closeModal();
    resolve?.(null);
  }

  function postToGuest(type, payload) {
    if (!iframeEl?.contentWindow) return;
    iframeEl.contentWindow.postMessage(makeEnvelope(type, payload, { token: frameToken }), '*');
  }

  function resolveMethod(method) {
    for (const capName of Object.keys(capabilities)) {
      const cap = capabilities[capName];
      if (cap.methods && Object.prototype.hasOwnProperty.call(cap.methods, method)) {
        return { capName, handler: cap.methods[method] };
      }
    }
    return null;
  }

  async function handleRpcCall({ callId, method, params }) {
    try {
      if (method === 'storage.get') {
        const result = await getExtensionStorageValue(extensionId, params.key);
        return { callId, result };
      }
      if (method === 'storage.set') {
        await setExtensionStorageValue(extensionId, params.key, params.value);
        return { callId, result: true };
      }
      if (method === 'ui.openModal') {
        // A guest-specified modal renders app-native, so it is a phishing
        // primitive, not a convenience: it must be consented to like any
        // other capability, BEFORE any of it is serviced. This check is
        // deliberately ahead of everything else in this branch.
        if (!grantedSet.has(UI_MODAL_CAPABILITY)) {
          throw new Error(
            `Extension is missing granted capability "${UI_MODAL_CAPABILITY}" for method "${method}"`
          );
        }
        // Asking the user to type a secret into a dialog the extension wrote
        // is a materially bigger ask than "show a form", so it has its own
        // consent row. Refused outright rather than downgraded to a plain
        // text field — silently un-masking a password the user is mid-way
        // through typing would be worse than failing the call.
        if (specRequestsPassword(params) && !grantedSet.has(UI_PASSWORD_FIELD_CAPABILITY)) {
          throw new Error(
            `Extension is missing granted capability "${UI_PASSWORD_FIELD_CAPABILITY}" to request a password field`
          );
        }
        supersedeOwnModal();
        modalSeq += 1;
        const requestId = `modal-${extensionId}-${modalSeq}-${Date.now()}`;
        // Never pre-empt a DIFFERENT extension's open modal: that swap is
        // what turns "extension shows a dialog" into "extension replaces the
        // dialog the user is already answering". openModal refuses instead.
        if (!useExtensionStore.getState().openModal(extensionId, requestId, params)) {
          throw new Error('Another extension already has a modal open');
        }
        activeModalRequestId = requestId;
        const result = await new Promise((resolve) => {
          modalResolvers.set(requestId, resolve);
        });
        return { callId, result };
      }
      // `ui.openTable` and `storage.*` remain deliberately ambient (no
      // capability): openTable can only target THIS extension's own
      // connections (extensionId is injected here, never taken from the
      // payload), and storage.* is a per-extension, size-capped, private
      // file. They are the permission-free surface by design — documented so
      // the next audit does not have to re-derive it.
      if (method === 'ui.openTable') {
        const tabId = openDbManagerTab({
          extensionId,
          connId: params.connId,
          engine: params.engine,
          dbName: params.dbName,
          tableName: params.tableName,
          label: params.label,
          pendingAction: params.pendingAction,
          // "Open in new tab": without it the deterministic key focuses the
          // tab this table is already in.
          newTab: !!params.newTab,
        });
        return { callId, result: tabId };
      }
      // `ui.openOverview`: a database's overview tab (tables, views, … and the
      // ER diagram — DBeaver's database editor). Ambient like openTable, for
      // the same reason: extensionId is injected, own connections only, and it
      // only reads the catalog.
      if (method === 'ui.openOverview') {
        const tabId = openDbOverviewTab({
          extensionId,
          connId: params.connId,
          engine: params.engine,
          dbName: typeof params.dbName === 'string' ? params.dbName : null,
          label: typeof params.label === 'string' ? params.label.slice(0, 80) : null,
          connLabel: typeof params.connLabel === 'string' ? params.connLabel.slice(0, 120) : null,
          mode: params.mode === 'diagram' ? 'diagram' : null,
        });
        return { callId, result: tabId };
      }
      // `ui.openQuery`: SQL the extension wants the user to SEE (Show CREATE)
      // opens in a query tab. Ambient like openTable, for the same reason
      // (extensionId injected, own connections only) — and never auto-run:
      // the user decides what executes.
      if (method === 'ui.openQuery') {
        const tabId = openDbManagerQueryTab({
          extensionId,
          connId: params.connId,
          engine: params.engine,
          dbName: params.dbName,
          sql: typeof params.sql === 'string' ? params.sql : '',
          label: typeof params.label === 'string' ? params.label.slice(0, 80) : null,
          scriptLinked: false,
          autoRun: false,
        });
        return { callId, result: tabId };
      }
      // `ui.refreshTable`: re-read the open grid tabs of one of this
      // extension's tables (after a TRUNCATE). Ambient like openTable, for the
      // same reason: extensionId is injected, so only its own tabs match.
      if (method === 'ui.refreshTable') {
        const count = reloadDbManagerTables({
          extensionId,
          connId: params.connId,
          dbName: params.dbName,
          tableName: params.tableName,
        });
        return { callId, result: count };
      }
      // `ui.notify`: the extension's frame is invisible, so a message it
      // puts on its own page reaches nobody — this shows it as an app
      // notification (text only, capped, attributed; see extensionNotify.js).
      if (method === 'ui.notify') {
        notifyFromExtension(extensionId, params);
        return { callId, result: true };
      }
      // `db.*` — connection storage, id-gated to the DB Manager.
      //
      // Ambient like ui.openTable rather than capability-gated, for the same
      // reason: extensionId is injected here and never taken from the payload,
      // so an extension can only ever reach its OWN connections. The id gate is
      // what keeps this off the general surface — the vault is the user's, not
      // something an arbitrary extension gets to write to.
      // An explicit set, NOT a `db.` prefix match. The engine calls the guest
      // makes are `db.<engine>.<method>` — db.postgres.connect, db.mysql.query
      // and so on — so a prefix test swallows every database call in the app
      // and answers "unknown method". These four are connection STORAGE, which
      // is a different thing that happens to share a namespace.
      if (DB_CONNECTION_METHODS.has(method)) {
        if (extensionId !== DB_MANAGER_EXTENSION_ID) {
          throw new Error(`${method} is not available to ${extensionId}`);
        }
        if (method === 'db.listSavedConnections') {
          const envelopes = await listDbConnectionEnvelopes();
          return {
            callId,
            result: {
              // null means locked — distinct from "you have none", which the
              // guest needs in order to show the right empty state.
              vaultLocked: envelopes === null,
              // Project scope (card 5.3): the active project's connections first.
              connections: orderDbConnections((envelopes || []).map((item) => descriptorFromVaultItem(item)).filter(Boolean)),
            },
          };
        }
        if (method === 'db.saveConnection') {
          try {
            const id = await saveDbConnectionToVault(params.connection);
            joinActiveProject(id, params.connection);
            return { callId, result: id };
          } catch (e) {
            // The user ticked "save this connection to the vault" while the
            // vault is locked. Silently not-saving honoured the lock but
            // betrayed the checkbox: the connection lived for the session and
            // vanished on restart, with nothing ever saying why. Ask for the
            // master passphrase instead — the same gate every other locked
            // surface opens — and finish the save the user asked for.
            const token = vaultErrorToken(e);
            const msg = String(e?.message || e);
            if (token !== 'passphrase_required' && !/vault_locked/.test(msg)) throw e;
            const unlocked = await new Promise((resolve) => {
              const done = (ok) => {
                window.removeEventListener(VAULT_GATE_UNLOCKED_EVENT, onOk);
                window.removeEventListener(VAULT_GATE_DISMISSED_EVENT, onNo);
                resolve(ok);
              };
              const onOk = () => done(true);
              const onNo = () => done(false);
              window.addEventListener(VAULT_GATE_UNLOCKED_EVENT, onOk);
              window.addEventListener(VAULT_GATE_DISMISSED_EVENT, onNo);
              openVaultGate('db-manager');
            });
            if (!unlocked) throw e; // dismissed: the guest keeps it session-only
            const id = await saveDbConnectionToVault(params.connection);
            joinActiveProject(id, params.connection);
            return { callId, result: id };
          }
        }
        if (method === 'db.connectSaved') {
          // The host dials, so the password never crosses into the guest
          // iframe. Rejects with db_password_required when there is none
          // stored, which the guest turns into a prompt and sends back here.
          const engine = await connectDbFromSavedConfig(extensionId, params.connId, {
            password: params.password,
          });
          return { callId, result: engine };
        }
        if (method === 'db.unlockVault') {
          // Same shape as the FTP panel's Unlock: try it, and route the
          // "this vault wants a passphrase" answers to the gate rather than
          // treating them as failures. Nothing pops up on its own — this only
          // runs when the user clicks Unlock.
          try {
            // Through the store rather than a bare invoke: a direct
            // `vault_unlock` opens the vault and leaves `status` stale, so the
            // rest of the app still reads locked. ensureUnlocked refreshes the
            // store AND announces via onVaultUnlocked, which is what clears
            // this extension's own locked row.
            await useVaultStore.getState().ensureUnlocked();
          } catch (e) {
            const token = vaultErrorToken(e);
            if (
              token === 'passphrase_required' ||
              token === 'vault_needs_bootstrap' ||
              token === 'vault_kdf_corrupt'
            ) {
              openVaultGate('db-manager');
              return { callId, result: false };
            }
            throw e;
          }
          return { callId, result: true };
        }
        if (method === 'db.savePassword') {
          await setDbConnectionPassword(params.connId, params.password);
          return { callId, result: true };
        }
        if (method === 'db.getConnectionDetails') {
          // The decrypted descriptor WITHOUT the password, so the edit form can
          // open on real values. descriptorFromVaultItem never copies the
          // secret — it only reports `remember` — so nothing sensitive crosses
          // into the iframe, which is the whole point of the host dialling.
          //
          // Without this the form opened on the LIST index, which carries no
          // TLS settings and no sqlite path: blank boxes and an sslMode
          // defaulting to 'prefer'. Submitting then wrote those blanks back,
          // silently downgrading verify_full and dropping certificate paths.
          const full = await readSavedDbConnection(extensionId, params.connId).catch(() => null);
          return { callId, result: full };
        }
        if (method === 'db.deleteConnection') {
          // Not just the vault item: a legacy connection also has a
          // storage.json entry and an OS keychain password, and leaving those
          // behind meant Remove did not remove — the row came back on the next
          // reload with its password still stored.
          await deleteSavedDbConnection(extensionId, params.connId);
          return { callId, result: true };
        }
        if (method === 'db.copyTableData') {
          // "Copy data to…" (dbCopyTable.js). Answers once the column check
          // passed and writing starts — a long copy must not outlive the RPC
          // timeout — then reports progress / the result itself.
          const started = await startTableCopy(extensionId, params);
          return { callId, result: started };
        }
        if (method === 'db.invalidateSchemaCache') {
          // Refresh in the sidebar: the SQL autocompletion's cached table and
          // column lists for this connection are dropped, so a table created
          // since shows up there too. Scoped to this extension's own connId.
          invalidateSchemaCache(extensionId, String(params?.connId ?? ''));
          return { callId, result: true };
        }
        throw new Error(`unknown method: ${method}`);
      }
      const resolved = resolveMethod(method);
      if (!resolved) throw new Error(`Unknown capability method: ${method}`);
      if (!grantedSet.has(resolved.capName)) {
        throw new Error(`Extension is missing granted capability "${resolved.capName}" for method "${method}"`);
      }
      const result = await resolved.handler(extensionId, params);
      return { callId, result };
    } catch (err) {
      return { callId, error: err?.message || String(err) };
    }
  }

  function onMessage(event) {
    // Mirror of the guest's two-control check (guestSdk.js): the frame
    // identity AND the per-mount token. The source check alone already stops
    // one guest impersonating another to the host; the token is what keeps
    // the guarantee if the frame topology ever changes.
    if (event.source !== iframeEl?.contentWindow) return;
    const data = event.data;
    if (!data || typeof data.type !== 'string') return;
    if (data.token !== frameToken) return;

    if (data.type === GUEST_TO_HOST.READY) {
      onGuestReady?.();
      return;
    }

    if (data.type === GUEST_TO_HOST.RPC_CALL) {
      const { callId, method } = data.payload;
      const timeout = interactiveMethods.has(method)
        ? null // still tracked in pendingTimeouts so the completion path stays uniform
        : setTimeout(() => {
            pendingTimeouts.delete(callId);
            postToGuest(HOST_TO_GUEST.RPC_RESULT, { callId, error: 'RPC call timed out' });
          }, RPC_CALL_TIMEOUT_MS);
      pendingTimeouts.set(callId, timeout);

      handleRpcCall(data.payload).then((res) => {
        if (!pendingTimeouts.has(callId)) return; // already timed out
        clearTimeout(pendingTimeouts.get(callId));
        pendingTimeouts.delete(callId);
        postToGuest(HOST_TO_GUEST.RPC_RESULT, res);
      });
      return;
    }

    if (data.type === GUEST_TO_HOST.TREE_PUBLISH) {
      onTreePublish?.(data.payload.sectionId, data.payload.nodes);
      return;
    }

    if (data.type === GUEST_TO_HOST.TREE_EXPAND) {
      const ids = treeExpandIds(data.payload?.nodeIds);
      if (typeof data.payload?.sectionId === 'string' && ids.length) onTreeExpand?.(data.payload.sectionId, ids);
      return;
    }

    if (data.type === GUEST_TO_HOST.ERROR) {
      onGuestError?.(data.payload?.message);
      return;
    }

    if (data.type === GUEST_TO_HOST.DISPOSED) {
      onGuestDisposedAck?.();
      return;
    }
  }

  window.addEventListener('message', onMessage);

  return {
    postToGuest,
    setGrantedCapabilities(nextCapabilities) {
      grantedSet = new Set(nextCapabilities);
    },
    resolveModalRequest(requestId, result) {
      const resolve = modalResolvers.get(requestId);
      if (!resolve) return;
      modalResolvers.delete(requestId);
      if (activeModalRequestId === requestId) activeModalRequestId = null;
      useExtensionStore.getState().closeModal();
      resolve(result);
    },
    destroy() {
      window.removeEventListener('message', onMessage);
      for (const timeout of pendingTimeouts.values()) clearTimeout(timeout);
      pendingTimeouts.clear();
      activeModalRequestId = null;
      // Any modal still awaiting user input when this bridge is torn down
      // (panel deactivated mid-prompt) must resolve so the guest promise
      // doesn't hang, and the store's pendingModal must not outlive it.
      if (modalResolvers.size > 0) {
        // Only clear the slot if what's on screen is OURS — another
        // extension's modal must survive this extension's teardown.
        if (useExtensionStore.getState().pendingModal?.extensionId === extensionId) {
          useExtensionStore.getState().closeModal();
        }
        for (const resolve of modalResolvers.values()) resolve(null);
        modalResolvers.clear();
      }
    },
  };
}
